import { Clock, AlertTriangle, CheckCircle2, Pencil, X } from "lucide-react";
import { Button, EmptyState } from "@dfws/ui";
import { ApiFailure } from "../../lib/api";
import { cancelOutbox, showComposer, toast, useOutbox } from "./composeData";
import s from "../settings/Settings.module.css";

/** Messages waiting to go out (scheduled, or retrying after a failure), and what went out today. */
export function ScheduledView() {
  const outbox = useOutbox();
  const items = outbox.data?.items ?? [];
  const waiting = items.filter((o) => o.status === "queued" || o.status === "sending");
  const failed = items.filter((o) => o.status === "failed");
  const sent = items.filter((o) => o.status === "sent");
  const when = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  const cancel = async (id: number) => {
    try {
      const draftId = await cancelOutbox(id);
      if (draftId) showComposer(draftId);
      toast({ text: "Cancelled. The draft is open so you can change it." });
    } catch (err) {
      toast({ text: err instanceof ApiFailure ? err.message : "That could not be cancelled.", tone: "error" });
    }
  };
  return (
    <div className={s.surface}>
      <div className={s.content}>
        <div className={s.inner}>
          <h1 className={s.title}>Scheduled</h1>
          <p className={s.lead}>Messages waiting to be sent. Cancel one to edit it again.</p>
          {!items.length && !outbox.isPending && (
            <EmptyState icon={<Clock />} title="Nothing scheduled">
              Use the arrow next to Send to pick a time.
            </EmptyState>
          )}
          {failed.length > 0 && (
            <section className={s.group} aria-labelledby="sch-failed">
              <h2 id="sch-failed" className={s.groupTitle}>Not sent</h2>
              <ul className={s.list}>
                {failed.map((o) => (
                  <li key={o.id} className={s.listItem}>
                    <AlertTriangle aria-hidden style={{ color: "var(--color-danger)", width: 18, flex: "none" }} />
                    <div className={s.listMain}>
                      <div className={s.listTitle}>{o.subject || "(no subject)"}</div>
                      <div className={s.listSub}>To {o.to.join(", ")}. {o.error ?? "The mail server refused it."}</div>
                    </div>
                    {o.draftId && <Button size="sm" icon={<Pencil />} onClick={() => showComposer(o.draftId!)}>Open draft</Button>}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {waiting.length > 0 && (
            <section className={s.group} aria-labelledby="sch-wait">
              <h2 id="sch-wait" className={s.groupTitle}>Waiting</h2>
              <ul className={s.list}>
                {waiting.map((o) => (
                  <li key={o.id} className={s.listItem}>
                    <Clock aria-hidden style={{ width: 18, flex: "none", color: "var(--color-text-muted)" }} />
                    <div className={s.listMain}>
                      <div className={s.listTitle}>{o.subject || "(no subject)"}</div>
                      <div className={s.listSub}>{o.status === "sending" ? "Sending now" : `Sends ${when(o.sendAt)}`} · To {o.to.join(", ")}</div>
                    </div>
                    {o.status === "queued" && <Button size="sm" icon={<X />} onClick={() => void cancel(o.id)}>Cancel</Button>}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {sent.length > 0 && (
            <section className={s.group} aria-labelledby="sch-sent">
              <h2 id="sch-sent" className={s.groupTitle}>Sent today</h2>
              <ul className={s.list}>
                {sent.map((o) => (
                  <li key={o.id} className={s.listItem}>
                    <CheckCircle2 aria-hidden style={{ width: 18, flex: "none", color: "var(--color-success)" }} />
                    <div className={s.listMain}>
                      <div className={s.listTitle}>{o.subject || "(no subject)"}</div>
                      <div className={s.listSub}>{when(o.sendAt)} · To {o.to.join(", ")}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
